/** Create / edit / delete a platform notice (react-hook-form + zod; WPS FE-03, backend P1-08).
 *
 *  A notice is ops' word on a problem on our side: the capability, the window (`ends_at` empty
 *  while it is still open) and the client wording — which the triage check `platform_notice`
 *  quotes verbatim to clients' AI after its own sentence ("From … to … we had a problem with
 *  <capability> … Our notice: <wording>"). The preview under the wording shows that sentence. The
 *  audience is every tenant, or the tenants listed (a franchise parent and each licensee are
 *  tenants of their own). The internal note is for ops only.
 *
 *  The page mounts the dialog per open, so the form reads its defaults once per notice (`key`
 *  remounts it for another id). An edit sends only the fields that changed (the audit row lists
 *  the fields sent); the reason travels as `X-Change-Reason` onto the Change history row. The backend refuses a wording that
 *  names anything internal, field-keyed: the message lands under the field. */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import * as noticesApi from '@/api/platformNotices';
import {
  INTERNAL_NOTE_MAX,
  MAX_TENANTS,
  PLATFORM_NOTICE_CONTENT_TYPE,
  platformNoticeKeys,
  WORDING_MAX,
  writeErrorsOf,
  type PlatformNotice,
  type PlatformNoticeAudience,
  type PlatformNoticeCapabilityKey,
  type PlatformNoticeWrite,
  type WriteField,
} from '@/api/platformNotices';
import { AppDialog } from '@/components/AppDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate } from '@/utils/format';

const FormSchema = z
  .object({
    capability: z.string().min(1, 'Pick what had the problem.'),
    starts_at: z.date({ invalid_type_error: 'When did it start?' }).nullable(),
    ends_at: z.date({ invalid_type_error: 'Not a valid time.' }).nullable(),
    wording: z
      .string()
      .trim()
      .min(1, 'Write what clients should hear.')
      .max(WORDING_MAX, `At most ${WORDING_MAX} characters.`),
    audience: z.enum(['all', 'listed']),
    tenants: z.array(z.object({ id: z.number(), name: z.string(), code: z.string().optional() })),
    internal_note: z.string().max(INTERNAL_NOTE_MAX, `At most ${INTERNAL_NOTE_MAX} characters.`),
  })
  .superRefine((v, ctx) => {
    if (!v.starts_at) {
      ctx.addIssue({ code: 'custom', path: ['starts_at'], message: 'When did it start?' });
    }
    if (v.starts_at && v.ends_at && v.ends_at.getTime() <= v.starts_at.getTime()) {
      ctx.addIssue({
        code: 'custom',
        path: ['ends_at'],
        message: 'The end must be after the start.',
      });
    }
    if (v.audience === 'listed' && v.tenants.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['tenants'], message: 'List at least one tenant.' });
    }
    if (v.audience === 'listed' && v.tenants.length > MAX_TENANTS) {
      ctx.addIssue({
        code: 'custom',
        path: ['tenants'],
        message: `At most ${MAX_TENANTS} tenants — a wider problem is one for every tenant.`,
      });
    }
  });
type FormValues = z.infer<typeof FormSchema>;

const asTenant = (t: { id: number; name: string; code?: string }): TenantPickerValue => ({
  id: t.id,
  name: t.name,
  ...(t.code ? { code: t.code } : {}),
});

function toDefaults(notice: PlatformNotice | null, pageTenants: TenantPickerValue[]): FormValues {
  if (!notice) {
    return {
      capability: '',
      starts_at: null,
      ends_at: null,
      wording: '',
      audience: 'all',
      // Listed only if the operator switches to "The tenants listed": the page's tenants are
      // there already.
      tenants: pageTenants.map((t) => ({ id: t.id, name: t.name, code: t.code ?? undefined })),
      internal_note: '',
    };
  }
  return {
    capability: notice.capability,
    starts_at: new Date(notice.starts_at),
    ends_at: notice.ends_at ? new Date(notice.ends_at) : null,
    wording: notice.wording,
    audience: notice.audience,
    tenants: notice.tenants.map((t) => ({ id: t.id, name: t.name, code: t.code })),
    internal_note: notice.internal_note,
  };
}

const sameTime = (a: Date | null, b: string | null) =>
  a === null ? b === null : b !== null && a.getTime() === new Date(b).getTime();
const sortedIds = (xs: { id: number }[]) => xs.map((x) => x.id).sort((a, b) => a - b);
const sameIds = (a: { id: number }[], b: { id: number }[]) =>
  sortedIds(a).join(',') === sortedIds(b).join(',');

/** What the form sends: everything on create; on edit only what changed. */
export function toWrite(v: FormValues, notice: PlatformNotice | null): PlatformNoticeWrite {
  const full: PlatformNoticeWrite = {
    capability: v.capability as PlatformNoticeCapabilityKey,
    starts_at: v.starts_at!.toISOString(),
    ends_at: v.ends_at ? v.ends_at.toISOString() : null,
    wording: v.wording.trim(),
    audience: v.audience,
    ...(v.audience === 'listed' ? { tenants: sortedIds(v.tenants) } : {}),
    internal_note: v.internal_note,
  };
  if (!notice) return full;
  const out: PlatformNoticeWrite = {};
  if (v.capability !== notice.capability) out.capability = full.capability;
  if (!sameTime(v.starts_at, notice.starts_at)) out.starts_at = full.starts_at;
  if (!sameTime(v.ends_at, notice.ends_at)) out.ends_at = full.ends_at ?? null;
  if (full.wording !== notice.wording) out.wording = full.wording;
  if (v.audience !== notice.audience) out.audience = v.audience;
  if (
    v.audience === 'listed' &&
    (v.audience !== notice.audience || !sameIds(v.tenants, notice.tenants))
  ) {
    out.tenants = sortedIds(v.tenants);
  }
  if (v.internal_note !== notice.internal_note) out.internal_note = v.internal_note;
  return out;
}

/** The check's own sentence around the wording — what a client's AI reads. */
function previewText(v: {
  label: string;
  starts_at: Date | null;
  ends_at: Date | null;
  wording: string;
}): string {
  const start = v.starts_at ? formatDate(v.starts_at.toISOString()) : '…';
  const what = v.label || '…';
  const wording = v.wording.trim() || '…';
  return v.ends_at
    ? `From ${start} to ${formatDate(v.ends_at.toISOString())} we had a problem with ${what}, while the bookings in the evidence were with us. Our notice: ${wording}`
    : `Since ${start} we have had a problem with ${what}, while the bookings in the evidence are with us. Our notice: ${wording}`;
}

interface Props {
  /** null = create; an id = edit (the dialog loads the notice: `?edit=<id>` links work). */
  noticeId: number | null;
  /** The page's tenants (its effective tenant filter): listed ready for a "listed" audience. */
  pageTenants?: TenantPickerValue[];
  onClose: () => void;
}

export function PlatformNoticeFormDialog({ noticeId, pageTenants = [], onClose }: Props) {
  const isEdit = noticeId !== null;
  const query = useQuery({
    queryKey: platformNoticeKeys.detail(noticeId ?? 0),
    queryFn: () => noticesApi.retrieve(noticeId!),
    enabled: isEdit,
  });

  if (isEdit && query.isPending) {
    return (
      <AppDialog open onClose={onClose} title="Platform notice">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </AppDialog>
    );
  }
  if (isEdit && query.isError) {
    return (
      <AppDialog open onClose={onClose} title="Platform notice">
        <Alert severity="error">Couldn’t load the notice: {query.error.message}</Alert>
      </AppDialog>
    );
  }
  return (
    <NoticeForm
      key={noticeId ?? 'new'}
      notice={isEdit ? (query.data ?? null) : null}
      pageTenants={pageTenants}
      onClose={onClose}
    />
  );
}

function NoticeForm({
  notice,
  pageTenants,
  onClose,
}: {
  notice: PlatformNotice | null;
  pageTenants: TenantPickerValue[];
  onClose: () => void;
}) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const isEdit = notice !== null;
  const showHistory = isEdit && hasPerm('console_audit_view');
  const [tab, setTab] = useState(0);
  const [reason, setReason] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [general, setGeneral] = useState<string | null>(null);

  const capabilities = useQuery({
    queryKey: platformNoticeKeys.capabilities,
    queryFn: noticesApi.capabilities,
    staleTime: 60 * 60_000,
  });
  const options = capabilities.data?.capabilities ?? [];

  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toDefaults(notice, pageTenants),
  });
  const [capability, startsAt, endsAt, wording, audience] = useWatch({
    control,
    name: ['capability', 'starts_at', 'ends_at', 'wording', 'audience'],
  });
  const label =
    options.find((o) => o.key === capability)?.label ??
    (notice && notice.capability === capability ? notice.capability_label : '');

  const done = () => {
    void qc.invalidateQueries({ queryKey: platformNoticeKeys.list });
    if (notice) void qc.invalidateQueries({ queryKey: platformNoticeKeys.detail(notice.id) });
    onClose();
  };

  const save = useMutation({
    mutationFn: (body: PlatformNoticeWrite) =>
      notice ? noticesApi.update(notice.id, body, reason) : noticesApi.create(body, reason),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Notice saved.' : 'Notice posted.', 'success');
      done();
    },
    onError: (e) => {
      const { fields, general: rest } = writeErrorsOf(e);
      for (const [field, message] of Object.entries(fields)) {
        setError(field as WriteField, { type: 'server', message });
      }
      setGeneral(rest);
    },
  });

  const remove = useMutation({
    mutationFn: () => noticesApi.remove(notice!.id),
    onSuccess: () => {
      snackbar.show('Notice deleted.', 'success');
      done();
    },
    onError: (e) => setGeneral(writeErrorsOf(e).general),
  });

  const submit = handleSubmit((v) => {
    setGeneral(null);
    const body = toWrite(v, notice);
    if (Object.keys(body).length === 0) {
      snackbar.show('Nothing changed.', 'info');
      onClose();
      return;
    }
    save.mutate(body);
  });

  const busy = save.isPending || remove.isPending;

  return (
    <>
      <AppDialog
        open
        onClose={onClose}
        size="md"
        disableBackdropClose
        title={isEdit ? `Platform notice #${notice.id}` : 'New platform notice'}
        actions={
          tab === 0 ? (
            <>
              {isEdit && (
                <Button
                  color="error"
                  disabled={busy}
                  onClick={() => setConfirmDelete(true)}
                  sx={{ mr: 'auto' }}
                >
                  Delete
                </Button>
              )}
              <Button onClick={onClose}>Cancel</Button>
              <Button variant="contained" disabled={busy} onClick={submit}>
                {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Post notice'}
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>Close</Button>
          )
        }
      >
        {showHistory && (
          <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mb: 1 }}>
            <Tab label="Editor" />
            <Tab label="Change history" />
          </Tabs>
        )}
        {tab === 1 && notice ? (
          <HistoryPanel
            contentType={PLATFORM_NOTICE_CONTENT_TYPE}
            objectPk={String(notice.id)}
            onRestored={() => void qc.invalidateQueries({ queryKey: platformNoticeKeys.all })}
          />
        ) : (
          <Stack component="form" noValidate spacing={2} sx={{ mt: 1 }} onSubmit={submit}>
            <Controller
              control={control}
              name="capability"
              render={({ field }) => (
                <TextField
                  select
                  size="small"
                  label="What had the problem"
                  value={options.length ? field.value : ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  error={Boolean(errors.capability)}
                  helperText={
                    errors.capability?.message ??
                    (capabilities.isError
                      ? 'Couldn’t load the capabilities.'
                      : 'In client words: the check says “a problem with …”.')
                  }
                >
                  {options.map((o) => (
                    <MenuItem key={o.key} value={o.key}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Controller
                  control={control}
                  name="starts_at"
                  render={({ field }) => (
                    <DateTimePicker
                      label="Started"
                      value={field.value}
                      onChange={(d) => field.onChange(d)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          required: true,
                          error: Boolean(errors.starts_at),
                          helperText: errors.starts_at?.message ?? 'Your local time.',
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="ends_at"
                  render={({ field }) => (
                    <DateTimePicker
                      label="Ended"
                      value={field.value}
                      onChange={(d) => field.onChange(d)}
                      slotProps={{
                        field: { clearable: true },
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          error: Boolean(errors.ends_at),
                          helperText:
                            errors.ends_at?.message ?? 'Empty while it is still going on.',
                        },
                      }}
                    />
                  )}
                />
              </Stack>
            </LocalizationProvider>

            <TextField
              {...register('wording')}
              size="small"
              label="Client wording"
              required
              multiline
              minRows={2}
              inputProps={{ maxLength: WORDING_MAX }}
              error={Boolean(errors.wording)}
              helperText={
                errors.wording?.message ??
                `Quoted verbatim to clients’ AI. Client words only: no internal names, hosts, code paths, secrets or staff addresses (${wording.length}/${WORDING_MAX}).`
              }
            />

            <Box
              data-notice-preview
              sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}
            >
              <Typography variant="caption" color="text.secondary" component="div">
                What a client’s AI reads
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {previewText({ label, starts_at: startsAt, ends_at: endsAt, wording })}
              </Typography>
            </Box>

            <Controller
              control={control}
              name="audience"
              render={({ field }) => (
                <Box>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Who it is for
                  </Typography>
                  <RadioGroup
                    row
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value as PlatformNoticeAudience)}
                  >
                    <FormControlLabel
                      value="all"
                      control={<Radio size="small" />}
                      label="Every tenant"
                    />
                    <FormControlLabel
                      value="listed"
                      control={<Radio size="small" />}
                      label="The tenants listed"
                    />
                  </RadioGroup>
                </Box>
              )}
            />
            {audience === 'listed' && (
              <Controller
                control={control}
                name="tenants"
                render={({ field }) => (
                  <Box data-notice-tenants>
                    <TenantPicker
                      multiple
                      label="Tenants"
                      placeholder="Pick the tenants"
                      fieldWidth="100%"
                      closedToggle={false}
                      value={field.value.map(asTenant)}
                      onChange={(next) =>
                        field.onChange(
                          next.map((t) => ({ id: t.id, name: t.name, code: t.code ?? undefined })),
                        )
                      }
                    />
                    {field.value.length > 0 && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{ mt: 1 }}
                      >
                        {field.value.map((t) => (
                          <Chip
                            key={t.id}
                            size="small"
                            label={
                              t.code ? `${t.name || t.code} (${t.code})` : t.name || `#${t.id}`
                            }
                            onDelete={() =>
                              field.onChange(field.value.filter((x) => x.id !== t.id))
                            }
                          />
                        ))}
                      </Stack>
                    )}
                    <Typography
                      variant="caption"
                      color={errors.tenants ? 'error' : 'text.secondary'}
                      component="div"
                      sx={{ mt: 0.5 }}
                    >
                      {errors.tenants?.message ??
                        'A franchise parent and each licensee are tenants of their own: list each one the problem touched.'}
                    </Typography>
                  </Box>
                )}
              />
            )}

            <TextField
              {...register('internal_note')}
              size="small"
              label="Internal note"
              multiline
              minRows={2}
              inputProps={{ maxLength: INTERNAL_NOTE_MAX }}
              error={Boolean(errors.internal_note)}
              helperText={
                errors.internal_note?.message ?? 'For ops only — never served to a client.'
              }
            />

            <TextField
              label="Reason for change"
              size="small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText="Optional — stored in the change history."
            />

            {isEdit && notice.created_by && (
              <Typography variant="caption" color="text.secondary">
                Posted by {notice.created_by.email ?? `user #${notice.created_by.id}`} on{' '}
                {formatDate(notice.created_ts)}; last saved {formatDate(notice.updated_ts)}. Every
                save is in the console audit log (<code>platform_notice.*</code>).
              </Typography>
            )}
            {general && <Alert severity="error">{general}</Alert>}
          </Stack>
        )}
      </AppDialog>
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this notice?"
        message="Clients’ AI stops quoting it at once. The deletion is audited (platform_notice.deleted)."
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          remove.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}
